import { useState } from "react";
import { Loader2, Sparkles, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const PRESETS = [
    { kind: "blog_ideas", label: "SEO Blog Ideas", desc: "5 keyword-optimised post titles", placeholder: "e.g. CBSE Class 10 math board exam prep" },
    { kind: "ad_copy", label: "Ad Headlines", desc: "5 Google/Facebook ad headlines", placeholder: "e.g. Home tuition in Jabalpur, class 10 math" },
    { kind: "email_subject", label: "Email Subjects", desc: "5 subject lines for parent outreach", placeholder: "e.g. Demo follow-up for registered parents" },
    { kind: "social_post", label: "Social Posts", desc: "3 ready-to-post captions", placeholder: "e.g. NEET toppers announcement post" },
];

export default function AiMarketingPanel() {
    const [kind, setKind] = useState("blog_ideas");
    const [context, setContext] = useState("");
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [copiedIdx, setCopiedIdx] = useState(null);

    const preset = PRESETS.find((p) => p.kind === kind) || PRESETS[0];

    const generate = async () => {
        setLoading(true);
        setSuggestions([]);
        try {
            const r = await api.post("/admin/ai/suggest", { kind, context });
            setSuggestions(r.data.suggestions || []);
            if (!(r.data.suggestions || []).length) toast.info("No suggestions returned. Try a different context.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "AI generation failed");
        } finally {
            setLoading(false);
        }
    };

    const copy = (text, idx) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedIdx(idx);
            setTimeout(() => setCopiedIdx(null), 1500);
        });
    };

    return (
        <div data-testid="admin-ai-marketing">
            <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <h2 className="font-heading text-2xl font-bold">Marketing Templates</h2>
                <span className="text-[10px] uppercase tracking-[0.18em] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold">Free</span>
            </div>
            <p className="text-sm text-slate-500 mb-5">Curated SEO blog titles, ad headlines, email subject lines and social posts — written specifically for a 1:1 home-tuition business. Click any preset, tweak the context, and hit Generate for fresh variations.</p>

            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
                <label className="text-xs font-semibold text-slate-600 mb-2 block">What do you need?</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
                    {PRESETS.map((p) => (
                        <button
                            key={p.kind}
                            onClick={() => setKind(p.kind)}
                            data-testid={`ai-preset-${p.kind}`}
                            className={`text-left p-3 rounded-xl border transition-all ${
                                kind === p.kind
                                    ? "border-[#1E3A8A] bg-blue-50/50 ring-2 ring-[#1E3A8A]/10"
                                    : "border-slate-200 hover:border-slate-300 bg-white"
                            }`}
                        >
                            <div className="font-heading font-semibold text-sm text-[#0F172A]">{p.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{p.desc}</div>
                        </button>
                    ))}
                </div>

                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Context (optional)</label>
                <textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    rows={2}
                    placeholder={preset.placeholder}
                    data-testid="ai-context"
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                />
                <div className="mt-3 flex justify-end">
                    <button
                        onClick={generate}
                        disabled={loading}
                        data-testid="ai-generate"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1E3A8A] text-white font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {loading ? "Generating…" : "Generate"}
                    </button>
                </div>
            </div>

            {suggestions.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ai-results">
                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">Suggestions</div>
                    <ul className="space-y-2">
                        {suggestions.map((s, idx) => (
                            <li key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                                <div className="flex-1 text-sm text-[#0F172A]">{s}</div>
                                <button
                                    onClick={() => copy(s, idx)}
                                    data-testid={`ai-copy-${idx}`}
                                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-[#1E3A8A] bg-white border border-slate-200 rounded-md hover:bg-blue-50"
                                >
                                    {copiedIdx === idx ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                    {copiedIdx === idx ? "Copied" : "Copy"}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
